import TextField, { TextFieldVariants } from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { useEffect, useRef, useState } from 'react';
import ProcedureList from '@src/cache/ProcedureList';
import ProcedureDTO from '@src/adapters/dto/procedure/ProcedureDTO';
import IdNameDto from '@src/adapters/dto/shared/IdNameDto';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
} from '@mui/material';
import { useCreateProcedureLazy } from '@src/adapters/hooks/procedures/useCreateProcedure';
import SpecialityAutoComplete from '../speciality/SpecialityAutoComplete';

interface Props {
  onSelection?(procedureId: string | null): void;
  selectedId: string | null;
  enableNew?: boolean;
  variant?: TextFieldVariants;
  disabled?: boolean;
}

function ProcedureAutoComplete({
  onSelection,
  selectedId,
  enableNew = false,
  variant,
  disabled,
}: Props) {
  const { execute: createItem } = useCreateProcedureLazy();
  const { getList: getProcedures } = ProcedureList();
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState<ProcedureDTO[]>([]);
  const [selected, setSelected] = useState<ProcedureDTO | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [newValue, setNewValue] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const parent = useRef(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ProcedureDTO>();

  useEffect(() => {
    getProcedures(selectedId || '').then((result) => {
      const items = result
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item) => ({
          id: item.id || '',
          name: item.name,
          specialiatyId: item.specialityId ? item.specialityId : '',
        }));
      setAvailable(items);
    });
  }, [getProcedures, selectedId]);

  useEffect(() => {
    if (selectedId) {
      const procedure = available.find((a) => a.id === selectedId);
      setSelected(procedure || null);
      setInputValue(procedure ? procedure.name : '');
    } else {
      setSelected(null);
      setInputValue('');
    }
  }, [available, selectedId]);

  const updateSelected = (value: ProcedureDTO | null) => {
    setSelected(value);
    setInputValue(value ? value.name : '');
    if (onSelection) {
      onSelection(value?.id || null);
    }
  };

  const handleAddNewItem = () => {
    setValue('name', newValue);
    setAddOpen(true);
  };

  const handleCancelAdd = () => {
    reset();
    setAddOpen(false);
  };

  const onSubmit = async (data: ProcedureDTO) => {
    const result = await createItem({ procedure: data });

    if (result.procedure) {
      reset();
      setAddOpen(false);
      const savedItem: ProcedureDTO = {
        id: result.procedure.id!,
        name: result.procedure.name,
        procedureShortName: result.procedure.procedureShortName,
        variantShortName: result.procedure.variantShortName,
        ccsdCode: result.procedure.ccsdCode,
        opcsCode: result.procedure.opcsCode,
        specialityId: result.procedure.specialityId,
      };
      if (onSelection) {
        onSelection(savedItem.id);
      }
    }
  };

  return (
    <>
      <Autocomplete
        fullWidth
        freeSolo={enableNew}
        selectOnFocus
        handleHomeEndKeys
        ref={parent}
        open={open}
        value={selected}
        inputValue={inputValue}
        disabled={disabled}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(event, value) => {
          if (value) {
            if ((value as IdNameDto).id) {
              updateSelected(value as ProcedureDTO);
            } else {
              handleAddNewItem();
            }
          } else {
            updateSelected(null);
          }
        }}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        filterOptions={(options, params) => {
          const { inputValue } = params;

          if (options) {
            if (!enableNew) {
              return options.filter((o) =>
                o.name.toLowerCase().includes(inputValue.toLowerCase())
              );
            } else {
              let newOption: IdNameDto | undefined = undefined;

              if (inputValue) {
                const isExisting = options.some(
                  (option) =>
                    option.name.toLowerCase() === inputValue.toLowerCase()
                );
                if (inputValue && !isExisting) {
                  setNewValue(inputValue);
                  newOption = { id: '', name: `Add "${inputValue}"` };
                }
              }

              let optionsToDisplay = options.filter((o) =>
                o && o.name
                  ? o.name.toLowerCase().includes(inputValue.toLowerCase())
                  : o
              );
              //Do we need to restrict number shown at first?
              //optionsToDisplay = [...optionsToDisplay].slice(0, 50);
              if (newOption) {
                optionsToDisplay.push(newOption);
              }

              return optionsToDisplay;
            }
          } else {
            return options;
          }
        }}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        getOptionLabel={(option) =>
          typeof option === 'string'
            ? option
            : 'name' in option
              ? option.name
              : option
        }
        options={[...available]}
        renderInput={(params) => (
          <TextField {...params} label="Procedure" variant={variant} />
        )}
      />
      <Popover
        open={addOpen}
        disableEnforceFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        anchorEl={parent ? parent?.current : null}>
        <DialogTitle>New Procedure</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Name" fullWidth margin="normal" />
              )}
            />
            {errors.name && <p>{errors.name.message}</p>}
            <Controller
              name="procedureShortName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Procedure Short Name"
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 150 }}
                />
              )}
            />
            {errors.procedureShortName && (
              <p>{errors.procedureShortName.message}</p>
            )}
            <Controller
              name="variant"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Variant"
                  fullWidth
                  margin="normal"
                />
              )}
            />
            {errors.variant && <p>{errors.variant.message}</p>}
            <Controller
              name="variantShortName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Variant Short Name"
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 150 }}
                />
              )}
            />
            {errors.variantShortName && (
              <p>{errors.variantShortName.message}</p>
            )}

            <Controller
              name="ccsdCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="CCSD Code"
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 150 }}
                />
              )}
            />
            {errors.ccsdCode && <p>{errors.ccsdCode.message}</p>}

            <Controller
              name="opcsCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="OPCS Code"
                  fullWidth
                  margin="normal"
                  inputProps={{ maxLength: 150 }}
                />
              )}
            />
            {errors.opcsCode && <p>{errors.opcsCode.message}</p>}
            <Box sx={(theme) => ({ paddingTop: theme.spacing(2) })}>
              <SpecialityAutoComplete control={control} name="specialityId" />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelAdd}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Add
            </Button>
          </DialogActions>
        </form>
      </Popover>
    </>
  );
}

export default ProcedureAutoComplete;
